/**
 * Recalibration status contract — the ONE vocabulary between the backend
 * tracking row and every client surface (Consent Seams sprint T2, 2026-09-16).
 *
 * Mirrors services/recalibration_status_contract.py PUBLIC_STATUSES.
 * tests/gate/test_recal_status_contract.py parses this file and asserts the two
 * lists match, so a new backend status cannot reach a client that does not
 * know it.
 *
 * Why: `awaiting_user_input` was added to the DB on 2026-07-11 and never taught
 * to the client. RecalibrationProgress polled it forever ("Still working —
 * 182m 38s", cory.haggart 2026-09-16).
 */
export const RECAL_PUBLIC_STATUSES = [
  'running',
  'stalled',
  'awaiting_input',
  'completed',
  'failed',
  'dismissed',
  'not_found',
] as const;

export type RecalPublicStatus = (typeof RECAL_PUBLIC_STATUSES)[number];

export function isRecalPublicStatus(status: unknown): status is RecalPublicStatus {
  return (
    typeof status === 'string' &&
    (RECAL_PUBLIC_STATUSES as readonly string[]).includes(status)
  );
}

/**
 * What a chat-history hydrator does with a carrier message once /status answers.
 *
 * - `skip_seed`  — never mount this tracking id again this session
 *                  (not_found, dismissed, or a status this build was never
 *                  taught: an old or flag-off server sending raw
 *                  `awaiting_user_input`, a newer server's new word). Seeding
 *                  is what stops a mount → class-kill → re-mount loop.
 * - `skip_park`  — `awaiting_input`: a parked question, not work. Skip THIS
 *                  carrier message only. The id is deliberately NOT seeded: a
 *                  propose → confirm arc reuses one tracking id, and the
 *                  confirm turn's dispatch marker (a new message) must still be
 *                  able to mount the strip for the real rebuild.
 * - `evaluate`   — running / stalled / failed / completed: the hydrator's
 *                  existing logic (mount, or the recent-completion check).
 *                  A missing status keeps the pre-contract mount-on-doubt.
 */
export type RecalHydrationDecision = 'skip_seed' | 'skip_park' | 'evaluate';

export function recalHydrationDecision(status: unknown): RecalHydrationDecision {
  if (typeof status !== 'string') return 'evaluate';
  if (status === 'awaiting_input') return 'skip_park';
  if (status === 'not_found' || status === 'dismissed') return 'skip_seed';
  if (!isRecalPublicStatus(status)) return 'skip_seed';
  return 'evaluate';
}
